import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { debounce } from 'lodash';

import emailSentIco from 'assets/images/email-new-pin-ico.png';
import IMG_SLICK from 'assets/images/logos/slick-logo-text.png';
import validateEmail from 'bl/validation/validateEmail.bl';
import Space from 'components/generic/Space.component';
import S from 'constants/string.const';
import { xeLogoutUser } from 'utils/cognito.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import withDefaultPrevented from 'utils/dom/withDefaultPrevented';
import { isEnvMobile } from 'utils/env/determine.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

import { clearLockError } from 'lib/actions';
import PinModalComponent from 'onboarding/common/PinModal';
import SlickSlider from 'user/common/SlickSlider';

import CN from './PinForm.module.scss';

const L = logger('PinForm');

const determinePinLength = ({ state }) => (state.pinCode || []).length || 0;

const PIN_LIMIT = 4;
const DEBOUNCE_TIME = 100;

class PinForm extends React.Component {
  handleLogout = withDefaultPrevented(() => void this.props.xeLogoutUser());
  keyboardRef = React.createRef();
  componentRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      pinCode: [],
      requestWithEmailModal: false,
      emailSentModal: false,
      inputValue: '',
      emailHasError: false,
    };
    this.handlePress = debounce(this.handlePress, DEBOUNCE_TIME);
  }

  componentDidMount() {
    if (this.componentRef.current) {
      this.componentRef.current.focus();
    }
  }

  handleKeyDown = (event) => {
    if (event.key.match(/[0-9]/)) {
      this.handlePress(event.key);
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.handleRemove();
    }
  };

  handleClose = () => void this.setState({ requestWithEmailModal: false, emailSentModal: false });

  handleEmailChange = (ev) => void this.setState({ inputValue: ev.target.value });

  handleGoToLogin = () => void this.props.history.push('/login');

  handleSendEmail = () =>
    void this.setState({ requestWithEmailModal: false }, () => this.setState({ emailSentModal: true }));

  handleValidateEmail = (value) => void this.setState({ emailHasError: !validateEmail({ value }) });

  handlePress = (num) => {
    // NOTE: using prevState we update the value of pinCode /setState for each handlePress even if it is very quickly.
    this.setState((prevState) => {
      const pinLength = determinePinLength({ state: prevState });
      const redo = pinLength >= PIN_LIMIT && !!this.props.lockError;

      if (redo) {
        this.props.clearLockError();
      }

      const pinCode = [...(redo ? [] : prevState.pinCode), String(num)];

      if (pinCode.length >= PIN_LIMIT) {
        this.props.onPin(pinCode.join(''));
      }

      return { pinCode };
    });
  };

  handleRemove = () => {
    this.setState((prevState) => {
      const pinCode = [...prevState.pinCode];
      pinCode.pop();
      return { pinCode };
    });
  };

  handleRemoveAll = () => {
    this.setState({ pinCode: [] });
  };

  render() {
    const { emailHasError, emailSentModal, inputValue, requestWithEmailModal } = this.state;
    const { lockError } = this.props;

    const pinLength = determinePinLength({ state: this.state });
    const wrongPin = lockError && pinLength >= PIN_LIMIT;
    const keyboard = this.keyboardRef && this.keyboardRef.current;
    const isMobile = isEnvMobile({ regex: true });

    L.debug('render()', { isMobile, pinLength, wrongPin, keyboard }, { props: this.props, state: this.state });

    if (isMobile && keyboard) {
      // FIXME: use try using React events instead of direct element manipulation
      // FIXME: get this out of render() where it adds listener on every render and use removeEventListener for it
      keyboard.addEventListener('touchmove', (e) => void (e.scale !== 1 && e.preventDefault()), { passive: false });
    }

    const containerClasses = [CN[`code-${pinLength}`]];

    const matchDotsClasses = cn({
      [CN.dotsContainer]: true,
      [CN.dotError]: wrongPin,
    });
    // S.empty stops React from complaining it got false instead of ''
    const emailLabelClasses = cn(S.empty, {
      'input-error': emailHasError,
    });

    return (
      <div
        data-bem="PinForm"
        className={CN.component}
        onKeyDown={this.handleKeyDown}
        ref={this.componentRef}
        tabIndex="0"
      >
        <div className={CN.layout}>
          <div data-bem="PinForm__content" className={CN.loginColumn}>
            <img src={IMG_SLICK} alt="Slick logo" className={CN.logo} />
            <div className={CN.title}>
              Enter your
              <span className={CN.bold}>
                <Space>4 digit pin</Space>
              </span>
              {S.userLogin}
            </div>

            <div className="login-wrapper">
              <div className="key-board-container" ref={this.keyboardRef}>
                <div className={containerClasses}>
                  <div className={matchDotsClasses}>
                    {[...Array(PIN_LIMIT)].map((value, index) => (
                      <div key={index} className={CN.dotContainer}>
                        <span className={CN.dot} />
                      </div>
                    ))}
                  </div>
                  <div className={CN.errorContainer}>
                    {wrongPin && <div className={CN.error}>Please enter a valid 4 digit pin code</div>}
                  </div>
                  <div className={CN.keyboardButtons}>
                    {[...Array(9)].map((value, index) => (
                      <button
                        key={index + 1}
                        onClick={!isMobile ? () => this.handlePress(index + 1) : alwaysNoop}
                        onTouchStart={isMobile ? () => this.handlePress(index + 1) : alwaysNoop}
                        className={CN.keyButton}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <div className={CN.emptySpace}></div>
                    <button
                      onClick={!isMobile ? () => this.handlePress(0) : alwaysNoop}
                      onTouchStart={isMobile ? () => this.handlePress(0) : alwaysNoop}
                      className={CN.keyButton}
                    >
                      0
                    </button>
                    <button onClick={wrongPin ? this.handleRemoveAll : this.handleRemove} className={CN.clearButton} />
                  </div>
                </div>
              </div>
            </div>
            <div className={CN.signInContainer}>
              <Link to="#" onClick={this.handleLogout} className={CN.link}>
                LOGIN WITH EMAIL
              </Link>
            </div>
          </div>
          <div className={CN.sliderColumn}>
            <SlickSlider />
          </div>
        </div>
        {requestWithEmailModal && (
          <PinModalComponent
            title="Please enter your email"
            message=""
            leftButtonText="Log In with Email Instead"
            onLeft={this.handleGoToLogin}
            rightButtonText=""
            onRight={() => {}}
            displayFooter
            displayXclose
            onXclick={
              this.handleClose // hidden with css
            }
            additionalClass="pin-login-with-email-modal"
          >
            <label
              data-bem="PinForm__email-form--modal"
              htmlFor="UserEmail"
              className={emailLabelClasses}
              style={{ position: 'relative' }}
            >
              {emailHasError ? (
                <span className="error" style={{ color: '#FF0F0F' }}>
                  The email you have entered is not registered on Slick*
                </span>
              ) : (
                <span className="field-name">Your email address</span>
              )}

              <input
                className="utl-full-width"
                id="UserEmail"
                type="text"
                placeholder="you@example.com"
                autoComplete="off"
                onChange={this.handleEmailChange}
                onBlur={this.handleValidateEmail}
                value={inputValue}
              />
              <button onClick={this.handleSendEmail} className="input-with-button" />
            </label>
          </PinModalComponent>
        )}

        {emailSentModal && (
          <PinModalComponent
            title="Email sent successfully!"
            message=""
            leftButtonText="Log In with Email Instead"
            onLeft={alwaysNoop}
            rightButtonText=""
            onRight={alwaysNoop}
            displayFooter
            displayXclose
            onXclick={this.handleClose}
            additionalClass="pin-login-with-email-modal"
          >
            <div data-bem="PinForm__email-success--modal" className="image-with-text">
              <div className="img-container">
                <img src={emailSentIco} alt="Email sent!" />
              </div>
              <div>
                <p> Reset pin email sent to: {inputValue} </p>
              </div>
            </div>
          </PinModalComponent>
        )}
      </div>
    );
  }
}

const PinFormHoc = withRouter(
  connect(
    (state) => ({
      lockError: state?.lib?.lockError,
    }),
    mapActionsToDispatchToProps({ clearLockError, xeLogoutUser }),
  )(PinForm),
);

export default PinFormHoc;
